/**
 * Light/dark theme.
 *
 * Three states rather than two: an explicit light or dark choice, or `system`,
 * which follows the device and keeps following it if the device setting
 * changes mid-session. The choice is applied to <html> before React renders
 * (see the inline script in index.html) so there is no flash of the wrong
 * theme on load.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'stockwise.theme';
const ThemeContext = createContext(null);

export const THEMES = ['light', 'dark', 'system'];

function systemPrefersDark() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function apply(theme) {
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
  // Tells the browser which scrollbar and form-control colours to use.
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  return dark;
}

function stored() {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(value) ? value : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(stored);
  const [isDark, setIsDark] = useState(() => apply(stored()));

  const setTheme = useCallback((next) => {
    setThemeState(next);
    setIsDark(apply(next));
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private browsing; the choice just will not persist.
    }
  }, []);

  // Only relevant while following the system, but the listener is cheap and
  // unsubscribing on every theme change would be more code than it saves.
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;

    const onChange = () => {
      if (theme === 'system') setIsDark(apply('system'));
    };

    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      isDark,
      setTheme,
      toggle: () => setTheme(isDark ? 'light' : 'dark'),
    }),
    [theme, isDark, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
}
